import { apiRequest } from './api';

// Admin API calls added on top of adminActivities.js: customers, GDPR erasure,
// bulk activity operations and plugin/payment settings.

// --- Customers -------------------------------------------------------------

export function fetchCustomers(search = '', { skip = 0, limit } = {}) {
  const query = { ...(search ? { search } : {}), skip };
  if (limit != null) query.limit = limit;
  return apiRequest('/api/admin/users', { auth: true, query });
}

// Real customers aggregated from bookings.customer by email — includes guest
// checkouts, which never get a db.users record (see server.py comment on the
// /admin/customers-from-bookings route).
export function fetchCustomersFromBookings(search = '', { skip = 0, limit } = {}) {
  const query = { ...(search ? { search } : {}), skip };
  if (limit != null) query.limit = limit;
  return apiRequest('/api/admin/customers-from-bookings', { auth: true, query });
}

export function setCustomerPassword(userId, newPassword) {
  return apiRequest(`/api/admin/users/${encodeURIComponent(userId)}/password`, {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ new_password: newPassword }),
  });
}

export function updateCustomer(userId, fields) {
  return apiRequest(`/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    auth: true,
    body: JSON.stringify(fields),
  });
}

// mode: 'anonymize' keeps booking records without personal data, 'purge' deletes them.
export function deleteCustomer(userId, mode = 'anonymize') {
  return apiRequest(`/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    auth: true,
    query: { mode },
  });
}

// --- Self-service (logged-in user) ----------------------------------------

export function updateMyProfile(fields) {
  return apiRequest('/api/me/profile', {
    method: 'PATCH',
    auth: true,
    body: JSON.stringify(fields),
  });
}

export function changeMyPassword(currentPassword, newPassword) {
  return apiRequest('/api/me/password', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export function deleteMyAccount(password, mode = 'anonymize') {
  return apiRequest('/api/me/delete', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ password, mode }),
  });
}

// --- Bulk activities -------------------------------------------------------

// action: activate | deactivate | feature | unfeature | price_percent | price_delta | price_set
export function bulkActivities(slugs, action, value) {
  return apiRequest('/api/admin/activities/bulk', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ slugs, action, ...(value !== undefined ? { value } : {}) }),
  });
}

// --- Destinations ----------------------------------------------------------

export function fetchDestinations() {
  return apiRequest('/api/admin/destinations', { auth: true });
}

export function saveDestination(slug, payload) {
  return apiRequest(`/api/admin/destinations/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(payload),
  });
}

export function deleteDestination(slug) {
  return apiRequest(`/api/admin/destinations/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    auth: true,
  });
}

// --- Plugins & payment settings -------------------------------------------

export function fetchPaymentSettings() {
  return apiRequest('/api/admin/settings/payments', { auth: true });
}

export function savePaymentSettings(settings) {
  return apiRequest('/api/admin/settings/payments', {
    method: 'PUT',
    auth: true,
    body: JSON.stringify(settings),
  });
}

export function fetchPlugins() {
  return apiRequest('/api/admin/plugins', { auth: true });
}

export function fetchEmailSettings() {
  return apiRequest('/api/admin/settings/email', { auth: true });
}

export function saveEmailSettings(settings) {
  return apiRequest('/api/admin/settings/email', {
    method: 'PUT', auth: true, body: JSON.stringify(settings),
  });
}

export function sendTestEmail(to) {
  return apiRequest('/api/admin/settings/email/test', {
    method: 'POST', auth: true, body: JSON.stringify({ to }),
  });
}

export function fetchStats() {
  return apiRequest('/api/admin/stats', { auth: true });
}

export function fetchSubscribers({ skip = 0, limit } = {}) {
  const query = { skip };
  if (limit != null) query.limit = limit;
  return apiRequest('/api/admin/newsletter', { auth: true, query });
}
export function deleteSubscriber(id) {
  return apiRequest(`/api/admin/newsletter/${id}`, { method: 'DELETE', auth: true });
}
export function sendCampaign(subject, body) {
  return apiRequest('/api/admin/newsletter/campaign', { method: 'POST', auth: true, body: JSON.stringify({ subject, body }) });
}

export function fetchCoupons() {
  return apiRequest('/api/admin/coupons', { auth: true });
}
export function createCoupon(coupon) {
  return apiRequest('/api/admin/coupons', { method: 'POST', auth: true, body: JSON.stringify(coupon) });
}
export function updateCoupon(id, coupon) {
  return apiRequest(`/api/admin/coupons/${id}`, { method: 'PUT', auth: true, body: JSON.stringify(coupon) });
}
export function deleteCoupon(id) {
  return apiRequest(`/api/admin/coupons/${id}`, { method: 'DELETE', auth: true });
}

export function fetchSocialSettings() {
  return apiRequest('/api/admin/settings/social', { auth: true });
}

export function saveSocialSettings(settings) {
  return apiRequest('/api/admin/settings/social', {
    method: 'PUT', auth: true, body: JSON.stringify(settings),
  });
}

export function fetchSeoSettings() {
  return apiRequest('/api/admin/settings/seo', { auth: true });
}

export function saveSeoSettings(settings) {
  return apiRequest('/api/admin/settings/seo', {
    method: 'PUT', auth: true, body: JSON.stringify(settings),
  });
}

export function fetchSocialFeedAdmin() {
  return apiRequest('/api/admin/social-feed', { auth: true });
}
export function addSocialFeedItem(item) {
  return apiRequest('/api/admin/social-feed', { method: 'POST', auth: true, body: JSON.stringify(item) });
}
export function deleteSocialFeedItem(id) {
  return apiRequest(`/api/admin/social-feed/${id}`, { method: 'DELETE', auth: true });
}
